// Group roll requests (issue 1049, DICE-010): the DM asks chosen players for a
// roll at once — "Reflex save: 1d20 + your modifier". The pure half: the ask's
// shape and parser, its reducer (an ask opens, a player's answer marks them,
// the DM closes it) and its projection.
//
// The design, kept simple: the ask FIXES the die and the quantity; the
// modifier is either fixed by the DM or, when null, each player's own, typed
// at the prompt (a save bonus differs per character and the table does not
// know it). A player answers ONCE, with `roll.request` carrying `ask`; the
// server builds the throw from the ask itself, so the label, die, quantity
// and privacy are the DM's, never the answer's. A private ask's results reach
// the DM and the roller alone; a public one's, the table.
//
// Hidden means absent (design/22): a player not asked receives nothing — no
// prompt, no result, no count. An asked player reads their own pending ask
// and nothing of who else was asked or has rolled; the DM reads it whole.
import { DICE_LIMITS, parseDie } from './dice.js';
export const ASK_LIMITS = { to: 30, open: 20 };
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUuid = (v) => typeof v === 'string' && UUID_RE.test(v);
const isInt = (v, min, max) => typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;
export const isAskBody = (b) => b.kind === 'roll.ask' || b.kind === 'roll.ask.close';
/** An ask from the wire or the log: exact shape or null. Untrusted input, always. */
export function parseAskSpec(value) {
    if (typeof value !== 'object' || value === null)
        return null;
    const a = value;
    const die = parseDie(a.die);
    if (!isUuid(a.id) || !die)
        return null;
    if (typeof a.label !== 'string' || a.label.trim().length < 1 || a.label.length > DICE_LIMITS.label)
        return null;
    if (!isInt(a.quantity, 1, DICE_LIMITS.quantity))
        return null;
    const modifier = a.modifier === undefined ? null : a.modifier;
    if (modifier !== null && !isInt(modifier, -DICE_LIMITS.modifier, DICE_LIMITS.modifier))
        return null;
    if (die.kind === 'coin' && modifier !== null && modifier !== 0)
        return null;
    if (!Array.isArray(a.to) || a.to.length < 1 || a.to.length > ASK_LIMITS.to || !a.to.every(isUuid) || new Set(a.to).size !== a.to.length)
        return null;
    if (typeof a.public !== 'boolean')
        return null;
    return { id: a.id, label: a.label.trim(), die, quantity: a.quantity, modifier: die.kind === 'coin' ? 0 : modifier, to: [...a.to], public: a.public };
}
export function parseAskBody(b) {
    if (b.kind === 'roll.ask') {
        const ask = parseAskSpec(b.ask);
        return ask ? { kind: 'roll.ask', ask } : null;
    }
    if (b.kind === 'roll.ask.close')
        return isUuid(b.id) ? { kind: 'roll.ask.close', id: b.id } : null;
    return null;
}
/** Stored asks from an older snapshot: each read through the parser, the answers kept only when well formed. */
export function normaliseAsks(value) {
    const out = {};
    if (typeof value !== 'object' || value === null)
        return out;
    for (const v of Object.values(value)) {
        const spec = parseAskSpec(v);
        if (!spec)
            continue;
        const raw = v.answered;
        const answered = {};
        if (typeof raw === 'object' && raw !== null)
            for (const [k, r] of Object.entries(raw))
                if (spec.to.includes(k) && isUuid(r))
                    answered[k] = r;
        out[spec.id] = { ...spec, answered };
    }
    return out;
}
/** The asks after an accepted event: opened, closed, or one player's answer marked (a roll carrying `ask`). */
export function reduceAsks(asks, body) {
    if (body.kind === 'roll.ask')
        return { ...asks, [body.ask.id]: { ...body.ask, to: [...body.ask.to], answered: {} } };
    if (body.kind === 'roll.ask.close') {
        if (!asks[body.id])
            return asks;
        const { [body.id]: _gone, ...rest } = asks;
        return rest;
    }
    const id = body.roll.ask;
    const ask = id ? asks[id] : undefined;
    if (!ask || body.roll.by || !ask.to.includes(body.roll.actor.id) || ask.answered[body.roll.actor.id])
        return asks;
    return { ...asks, [ask.id]: { ...ask, answered: { ...ask.answered, [body.roll.actor.id]: body.roll.id } } };
}
const forPlayer = (a) => ({ id: a.id, label: a.label, die: a.die, quantity: a.quantity, modifier: a.modifier, public: a.public });
/** The asks a viewer may read: the DM every open ask whole; a player only those asked of them that they have not answered. */
export function projectAsks(asks, viewer) {
    const all = Object.values(asks);
    if (viewer.role === 'dm')
        return all.map((a) => ({ ...a, to: [...a.to], answered: { ...a.answered } }));
    return all.filter((a) => a.to.includes(viewer.id) && !a.answered[viewer.id]).map(forPlayer);
}
/** What a player receives for an ask event: their prompt, or its withdrawal — and nothing at all when not asked. */
export function projectAskEvent(body, before, viewerId) {
    if (body.kind === 'roll.ask')
        return body.ask.to.includes(viewerId) ? { kind: 'ask.set', ask: forPlayer({ ...body.ask, answered: {} }) } : null;
    const was = before[body.id];
    return was && was.to.includes(viewerId) && !was.answered[viewerId] ? { kind: 'ask.clear', id: body.id } : null;
}
/**
 * The throw an answer makes, built from the ASK (the server's call): the DM's die, quantity, label and privacy; the
 * modifier the DM's when fixed, else the player's own. A refusal in words when this person may not answer it.
 */
export function answerRequest(ask, playerId, modifier) {
    if (!ask || !ask.to.includes(playerId))
        return 'That roll was not asked of you.';
    if (ask.answered[playerId])
        return 'You have already rolled for that.';
    return {
        kind: 'roll.request', die: ask.die, quantity: ask.quantity, modifier: ask.die.kind === 'coin' ? 0 : ask.modifier ?? modifier,
        label: ask.label, private: !ask.public, forUserId: null, ask: ask.id,
    };
}
//# sourceMappingURL=asks.js.map